import { Controller } from "@hotwired/stimulus";
import walletService from "../../../services/WalletService";
import { emitCheckoutEvent } from "../../../services/checkoutEvents";
import { storage } from "../../../storageconstants";
import { debugError, debugLog } from "../../../utils";
import { errorToastMessages, isUserCancellation, showErrorCard, showErrorToast } from "../../../errorsurfaces";

export default class extends Controller {
  static values = {
    disableDuration: 30,
    chainid: Number,
    session: String,
    spenderaddress: String,
    ispurchasesession: Boolean,
    tokencontractaddress: String,
  }

  async connect() {
    // Proactively prompt the network switch on load. ensureOnChain never throws,
    // so a rejection here can't strand the loading screen — the real enforcement
    // and retry happen in handleLocalApproval.
    if (walletService.getWalletClient()) {
      await walletService.ensureOnChain(this.chainidValue);
    }

    this.hideLoadingScreen();
  }

  async handleLocalApproval() {
    this.showLoadingScreen();
    this.disableInteractionForDuration();

    try {
      // viem's writeContract asserts wallet-chain == target-chain. If the user
      // rejected the switch, unblock the UI and bail so they can retry instead
      // of throwing ChainMismatchError.
      const onTargetChain = await walletService.ensureOnChain(this.chainidValue);
      if (!onTargetChain) {
        debugLog("Wallet not on target chain after switch attempt; aborting approval");
        emitCheckoutEvent("checkout_cw_tx_rejected", {
          wallet_provider: walletService.getConnectedWalletLabel() || "",
        });
        this.resetInteraction();
        showErrorCard("network-switch-needed", { session: this.sessionValue });
        return;
      }

      // Re-fetch after switching — the client is pinned to the chain it was built on.
      const walletClient = walletService.getWalletClient();
      const contractAddress = this.tokencontractaddressValue;
      const walletAddress = walletService.getWalletAddress();

      const ABI = [
        {
          constant: false,
          inputs: [
              {
              name: "_spender",
              type: "address",
              },
              {
              name: "_value",
              type: "uint256",
              },
          ],
          name: "approve",
          outputs: [
              {
              name: "",
              type: "bool",
              },
          ],
          payable: false,
          stateMutability: "nonpayable",
          type: "function",
        },
      ];

      debugLog("chainID:", this.chainidValue);
      debugLog("contractAddress:", contractAddress);
      debugLog("walletAddress:", walletAddress);
      debugLog("this.spenderaddressValue:", this.spenderaddressValue);

      // For Hedera (chain ID 295=mainnet, 296=testnet), use $1 billion approval
      // HTS tokens don't support infinite approvals (max uint256)
      let approvalAmount;
      if (this.chainidValue === 295 || this.chainidValue === 296) {
        approvalAmount = "100000000000000000"; // $100B for USDC (6 decimals)
        debugLog("Hedera network detected, using $1B approval:", approvalAmount);
      } else {
        approvalAmount = process.env.PARCEL_MAX_AMOUNT;
        debugLog("Non-Hedera network, using max uint256:", approvalAmount);
      }

      const chain = walletService.getChain(this.chainidValue);
      debugLog("Chain object:", chain);

      const txhash = await walletClient.writeContract({
        account: walletAddress,
        address: contractAddress,
        abi: ABI,
        functionName: "approve",
        args: [
          this.spenderaddressValue,
          approvalAmount,
        ],
        chain: chain,
      });

      debugLog("txhash:", txhash);

      emitCheckoutEvent("checkout_cw_tx_approved", {
        network: walletService.getChain(this.chainidValue)?.name || String(this.chainidValue || ""),
        tx_hash: txhash || "",
      });

      const data = {
        txhash: txhash,
        tokencontractaddress: this.tokencontractaddressValue,
        ispurchasesession: this.ispurchasesessionValue,
        session: this.sessionValue,
      };

      const form = document.getElementById("frame-post-form");
      form.action = `/${sessionStorage.getItem(storage.CODE)}/authorized-3rdParty-wallet-review/`;

      document.getElementById("form_txhash").value = JSON.stringify(data.txhash);
      document.getElementById("form_tokencontractaddress").value = data.tokencontractaddress;
      document.getElementById("form_ispurchasesession").value = data.ispurchasesession;
      document.getElementById("form_session").value = data.session;

      form.requestSubmit();
    } catch (error) {
      emitCheckoutEvent("checkout_cw_tx_rejected", {
        wallet_provider: walletService.getConnectedWalletLabel() || "",
      });
      debugError("Failed to approve locally:", error);
      this.resetInteraction();
      if (isUserCancellation(error)) {
        showErrorToast(errorToastMessages.paymentDeclinedInWallet);
      } else {
        showErrorCard("something-went-wrong", { session: this.sessionValue });
      }
    }
  }

  disconnect() {
    this.clearDisableInterval();
  }

  clearDisableInterval() {
    if (this.disableInterval) {
      clearInterval(this.disableInterval);
      this.disableInterval = null;
    }
  }

  resetInteraction() {
    this.clearDisableInterval();
    this.hideLoadingScreen();
  }

  disableInteractionForDuration() {
    const self = this;
    let remaining = this.disableDurationValue;

    self.disableInterval = setInterval(function() {
      remaining = remaining - 1;
      self.updateLoadingScreenMessage(`Please wait for ${remaining} second${remaining < 2 ? '' : 's'} before trying again.`);

      if (remaining === 0) {
        self.resetInteraction();
      }
    }, 1000);
  }

  updateLoadingScreenMessage(text) {
    this.dispatch("updateLoadingScreenMessage", { detail: { text } });
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }
}
